import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../hooks/useApi';
import { formatCurrency, formatDate, statusColors } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Search, FileText, MoreVertical, Trash2, Edit, Copy, ArrowUpDown,
  Newspaper, Globe, Upload, X, ChevronsUpDown, Check, CalendarRange
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import OrderModal from '../components/orders/OrderModal';
import NewsdeskSentInfo, { SentPill, SentControl } from '../components/NewsdeskSentInfo';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const isImageFile = (filename) => {
  if (!filename) return false;
  const ext = filename.toLowerCase().split('.').pop();
  return ['jpg', 'jpeg', 'png'].includes(ext);
};

const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [editions, setEditions] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editionFilter, setEditionFilter] = useState('all');
  const [advertiserFilter, setAdvertiserFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [settings, setSettings] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [sortOrder, setSortOrder] = useState(null); // null, 'asc', 'desc'

  useEffect(() => {
    loadData();
  }, []);

  // /bookings?open=<booking id> (the Digital calendar's "Open full
  // booking" button) opens that booking's form once the list has loaded,
  // then drops the parameter so a refresh or closing the form doesn't
  // reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('open');
  useEffect(() => {
    if (!openId || loading) return;
    const target = bookings.find((b) => b.id === openId);
    if (target) {
      setSelectedBooking(target);
      setShowCreateModal(true);
    }
    setSearchParams({}, { replace: true });
  }, [openId, loading, bookings, setSearchParams]);

  const loadData = async () => {
    try {
      const [bookingsData, advertisersData, editionsData, productTypesData, settingsData, salesRepsData] = await Promise.all([
        api.get('/bookings'),
        api.get('/advertisers'),
        api.get('/editions'),
        api.get('/product-types'),
        api.get('/settings'),
        api.get('/sales-reps')
      ]);
      setBookings(bookingsData);
      setAdvertisers(advertisersData);
      setEditions(editionsData);
      setProductTypes(productTypesData);
      setSalesReps(salesRepsData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = useMemo(() => {
    let result = bookings.filter(booking => {
      const matchesSearch = 
        booking.advertiser_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.edition_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
      const matchesEdition = editionFilter === 'all' || booking.edition_id === editionFilter;
      const matchesAdvertiser = advertiserFilter === 'all' || booking.advertiser_id === advertiserFilter;
      const matchesProduct = productFilter === 'all' || booking.product_type === productFilter;
      return matchesSearch && matchesStatus && matchesEdition && matchesAdvertiser && matchesProduct;
    });
    if (sortOrder) {
      result = [...result].sort((a, b) => {
        const cmp = (a.advertiser_name || '').localeCompare(b.advertiser_name || '');
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [bookings, searchQuery, statusFilter, editionFilter, advertiserFilter, productFilter, sortOrder]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await api.put(`/bookings/${bookingId}`, { status: newStatus });
      loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await api.delete(`/bookings/${bookingId}`);
      loadData();
    } catch (error) {
      console.error('Failed to delete booking:', error);
    }
  };

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visibleIds) => {
    setSelectedIds(prev => {
      if (visibleIds.every(id => prev.has(id))) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} booking${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.allSettled(ids.map(id => api.delete(`/bookings/${id}`)));
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      alert('Some deletions failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBulkDeleting(false);
    }
  };

  const duplicateBooking = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/duplicate`);
      loadData();
    } catch (error) {
      console.error('Failed to duplicate booking:', error);
      alert('Failed to duplicate booking.');
    }
  };

  const [bulkFromBooking, setBulkFromBooking] = useState(null);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'production', label: 'Production' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'paid', label: 'Paid' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <div className="space-y-6" data-testid="bookings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Bookings</h1>
          <p className="text-[#94A3B8] mt-1">Manage ad bookings and placements</p>
        </div>
        <Button 
          onClick={() => setShowOrderModal(true)}
          data-testid="create-booking-btn"
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Order
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              data-testid="booking-search"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#121212] border-[#1E293B] text-[#F8FAFC] placeholder:text-[#475569]"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B]">
              {statusOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-[#F8FAFC]">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <Select value={editionFilter} onValueChange={setEditionFilter}>
            <SelectTrigger className="w-[200px] bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="edition-filter">
              <SelectValue placeholder="All Editions" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">All Editions</SelectItem>
              {[...editions].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })).map(ed => (
                <SelectItem key={ed.id} value={ed.id} className="text-[#F8FAFC]">
                  {ed.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={advertiserFilter} onValueChange={setAdvertiserFilter}>
            <SelectTrigger className="w-[200px] bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="advertiser-filter">
              <SelectValue placeholder="All Advertisers" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">All Advertisers</SelectItem>
              {[...advertisers].sort((a, b) => a.business_name.localeCompare(b.business_name)).map(adv => (
                <SelectItem key={adv.id} value={adv.id} className="text-[#F8FAFC]">
                  {adv.business_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger className="w-[200px] bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="product-filter">
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">All Products</SelectItem>
              {[...new Set(bookings.map(b => b.product_type).filter(Boolean))].sort().map(pt => (
                <SelectItem key={pt} value={pt} className="text-[#F8FAFC]">
                  {pt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bookings Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
        </div>
      ) : filteredBookings.length > 0 ? (
        <div className="bg-[#121212] border border-[#1E293B] rounded-md overflow-hidden">
          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between px-4 py-2 bg-[#3B82F6]/10 border-b border-[#3B82F6]/30" data-testid="bookings-bulk-bar">
              <span className="text-sm text-[#F8FAFC]">
                <strong className="text-[#3B82F6]">{selectedIds.size}</strong> selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-[#94A3B8] hover:text-[#F8FAFC] h-8"
                  data-testid="bookings-bulk-clear"
                >Clear</Button>
                <Button
                  size="sm"
                  onClick={bulkDelete}
                  disabled={bulkDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white h-8"
                  data-testid="bookings-bulk-delete"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E293B] bg-[#0A0A0A]/80">
                  <th className="w-10 py-3 px-3 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[#3B82F6]"
                      checked={filteredBookings.length > 0 && filteredBookings.every(b => selectedIds.has(b.id))}
                      onChange={() => toggleSelectAll(filteredBookings.map(b => b.id))}
                      data-testid="bookings-select-all-checkbox"
                      title="Select all visible"
                    />
                  </th>
                  <th 
                    className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8] cursor-pointer hover:text-[#F8FAFC] select-none"
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc')}
                    data-testid="sort-advertiser-header"
                  >
                    <span className="flex items-center gap-1">
                      Advertiser
                      <ArrowUpDown className={`w-3 h-3 ${sortOrder ? 'text-[#3B82F6]' : 'opacity-40'}`} />
                      {sortOrder && <span className="text-[9px] text-[#3B82F6]">{sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>}
                    </span>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Edition</th>
                  <th className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Product</th>
                  <th className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Rate</th>
                  <th className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Sales Rep</th>
                  <th className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Creative</th>
                  <th className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Placement</th>
                  <th className="text-right py-3 px-4 text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {filteredBookings.map((booking) => (
                  <tr 
                    key={booking.id} 
                    className={`hover:bg-[#1A1A1A] transition-colors ${selectedIds.has(booking.id) ? 'bg-[#3B82F6]/10' : ''}`}
                    data-testid={`booking-row-${booking.id}`}
                  >
                    <td className="w-10 py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#3B82F6]"
                        checked={selectedIds.has(booking.id)}
                        onChange={() => toggleSelect(booking.id)}
                        data-testid={`bookings-select-${booking.id}`}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <button
                        className="font-medium text-[#F8FAFC] hover:text-[#3B82F6] transition-colors text-left"
                        onClick={() => { setSelectedBooking(booking); setShowCreateModal(true); }}
                        data-testid={`booking-name-${booking.id}`}
                      >
                        {booking.advertiser_name}
                      </button>
                      <p className="text-xs text-[#94A3B8]">{formatDate(booking.created_at)}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-[#F8FAFC]">{booking.edition_name}</p>
                      <p className="text-xs text-[#94A3B8]">{formatDate(booking.publish_date)}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-[#F8FAFC]">{booking.product_type?.replace('_', ' ')}</p>
                      <p className="text-xs text-[#94A3B8]">{booking.size}</p>
                      <SentPill booking={booking} />
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-medium text-[#F8FAFC]">{formatCurrency(booking.rate)}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-[#94A3B8]">{salesReps.find(r => r.id === booking.sales_rep_id)?.name || '-'}</p>
                    </td>
                    <td className="py-3 px-4">
                      {booking.creative_file ? (
                        isImageFile(booking.creative_filename) ? (
                          <img 
                            src={`${API_BASE}${booking.creative_file}`}
                            alt="Ad creative"
                            className="w-10 h-10 object-cover rounded border border-[#1E293B]"
                            data-testid={`booking-thumb-${booking.id}`}
                          />
                        ) : (
                          <div className="flex items-center gap-1 text-[#3B82F6]" data-testid={`booking-file-${booking.id}`}>
                            <FileText className="w-4 h-4" />
                            <span className="text-xs">PDF</span>
                          </div>
                        )
                      ) : (
                        <span className="text-xs text-[#475569]">None</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Select
                        value={booking.status}
                        onValueChange={(value) => updateBookingStatus(booking.id, value)}
                      >
                        <SelectTrigger
                          className={`h-7 w-auto min-w-[120px] border-0 bg-transparent px-2 py-0 text-xs rounded-full focus:ring-0 focus:ring-offset-0 ${statusColors[booking.status] || statusColors.draft}`}
                          data-testid={`status-select-${booking.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#1E293B]">
                          {statusOptions.filter(o => o.value !== 'all').map(opt => (
                            <SelectItem key={opt.value} value={opt.value} className="text-[#F8FAFC] text-xs">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      {booking.page_number ? (
                        <p className="text-[#F8FAFC]">
                          Page {booking.page_number} - {booking.position}
                        </p>
                      ) : (
                        <span className="text-[#94A3B8] text-sm">Not placed</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`booking-menu-${booking.id}`}>
                            <MoreVertical className="h-4 w-4 text-[#94A3B8]" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#121212] border-[#1E293B]">
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedBooking(booking);
                              setShowCreateModal(true);
                            }}
                            className="text-[#F8FAFC] focus:bg-[#1E293B]"
                          >
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => duplicateBooking(booking.id)}
                            className="text-[#F8FAFC] focus:bg-[#1E293B]"
                            data-testid={`duplicate-booking-${booking.id}`}
                          >
                            <Copy className="w-4 h-4 mr-2" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setBulkFromBooking(booking)}
                            className="text-[#F8FAFC] focus:bg-[#1E293B]"
                            data-testid={`bulk-duplicate-booking-${booking.id}`}
                          >
                            <CalendarRange className="w-4 h-4 mr-2" /> Duplicate across editions…
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#1E293B]" />
                          <DropdownMenuItem 
                            onClick={() => deleteBooking(booking.id)}
                            className="text-red-400 focus:bg-[#1E293B]"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#3B82F6]/30 bg-[#0F172A]">
                  <td></td>
                  <td className="py-3 px-4 text-xs font-bold text-[#3B82F6] uppercase" colSpan={3} data-testid="bookings-total-label">
                    Total ({filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''})
                  </td>
                  <td className="py-3 px-4" data-testid="bookings-total-amount">
                    <p className="font-bold text-[#3B82F6]">{formatCurrency(filteredBookings.reduce((sum, b) => sum + (b.rate || 0), 0))}</p>
                  </td>
                  <td colSpan={5}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-[#94A3B8]">
          <FileText className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg">No bookings found</p>
          <Button 
            onClick={() => setShowOrderModal(true)} 
            variant="outline" 
            className="mt-4 border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]"
          >
            Create your first order
          </Button>
        </div>
      )}

      {/* Create/Edit Booking Modal (edit existing bookings) */}
      <BookingModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedBooking(null);
        }}
        booking={selectedBooking}
        advertisers={advertisers}
        editions={editions}
        productTypes={productTypes}
        settings={settings}
        salesReps={salesReps}
        onSave={() => {
          loadData();
          setShowCreateModal(false);
          setSelectedBooking(null);
        }}
      />

      {/* New Order Modal (multi-item create flow) */}
      <OrderModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        advertisers={advertisers}
        editions={editions}
        productTypes={productTypes}
        salesReps={salesReps}
        settings={settings}
        onSave={() => { setShowOrderModal(false); loadData(); }}
      />

      {/* Duplicate-Across-Editions Modal */}
      <BulkBookingModal
        booking={bulkFromBooking}
        editions={editions}
        onClose={() => setBulkFromBooking(null)}
        onSave={() => { setBulkFromBooking(null); loadData(); }}
      />
    </div>
  );
};

// Duplicate-Across-Editions Modal
const BulkBookingModal = ({ booking, editions, onClose, onSave }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [periodDays, setPeriodDays] = useState(14);
  const [count, setCount] = useState(6);
  const [keepPosition, setKeepPosition] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (booking) { setSelectedIds([]); setPeriodDays(14); setCount(6); setKeepPosition(false); }
  }, [booking]);

  if (!booking) return null;

  const sourceEdition = editions.find(e => e.id === booking.edition_id);
  const sourceDate = sourceEdition?.publish_date || booking.publish_date;

  // Editions strictly after the source, sorted by date
  const futureEditions = [...editions]
    .filter(e => e.publish_date && (!sourceDate || e.publish_date > sourceDate))
    .sort((a, b) => (a.publish_date || '').localeCompare(b.publish_date || ''));

  const toggleOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const autoFillByPeriod = () => {
    if (!sourceDate) return;
    const start = new Date(sourceDate + 'T00:00:00');
    const targets = [];
    for (let i = 1; i <= count; i++) {
      const t = new Date(start); t.setDate(t.getDate() + periodDays * i);
      targets.push(t.toISOString().slice(0, 10));
    }
    // For each target date, find the closest edition by publish_date (within ±3 days)
    const chosen = [];
    for (const tgt of targets) {
      const tgtTime = new Date(tgt + 'T00:00:00').getTime();
      let best = null; let bestDelta = Infinity;
      for (const ed of futureEditions) {
        const delta = Math.abs(new Date(ed.publish_date + 'T00:00:00').getTime() - tgtTime);
        if (delta < bestDelta) { bestDelta = delta; best = ed; }
      }
      if (best && bestDelta <= 3 * 24 * 3600 * 1000 && !chosen.includes(best.id)) {
        chosen.push(best.id);
      }
    }
    setSelectedIds(chosen);
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    try {
      await api.post(`/bookings/${booking.id}/duplicate-across`, { edition_ids: selectedIds, keep_position: keepPosition });
      onSave();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-booking-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange className="w-5 h-5 text-[#3B82F6]" /> Duplicate Across Editions</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-[#94A3B8] bg-[#0A0A0A] border border-[#1E293B] rounded-md p-3">
            Source: <span className="text-[#F8FAFC] font-medium">{booking.advertiser_name}</span> · {booking.product_type} · <span className="text-[#94A3B8]">{booking.edition_name}</span>
          </div>

          <div className="grid grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Every N Days</Label>
              <Input type="number" min="1" value={periodDays} onChange={(e) => setPeriodDays(e.target.value)} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="bulk-bk-period" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Count</Label>
              <Input type="number" min="1" max="52" value={count} onChange={(e) => setCount(e.target.value)} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="bulk-bk-count" />
            </div>
            <Button type="button" variant="outline" onClick={autoFillByPeriod} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="bulk-bk-autofill">
              Auto-select by period
            </Button>
          </div>

          <div>
            <Label className="text-[#94A3B8] text-xs mb-2 block">
              Target editions ({selectedIds.length} selected)
            </Label>
            <div className="max-h-64 overflow-y-auto border border-[#1E293B] rounded-md divide-y divide-[#1E293B]" data-testid="bulk-bk-editions-list">
              {futureEditions.length === 0 ? (
                <p className="text-sm text-[#475569] p-3">No future editions available.</p>
              ) : (
                futureEditions.map(ed => (
                  <label key={ed.id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#1E293B] cursor-pointer">
                    <input type="checkbox" checked={selectedIds.includes(ed.id)} onChange={() => toggleOne(ed.id)} className="h-4 w-4 accent-[#3B82F6]" />
                    <span className="flex-1 text-sm text-[#F8FAFC]">{ed.name}</span>
                    <span className="text-xs text-[#94A3B8]">{ed.publish_date}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-[#F8FAFC] bg-[#0A0A0A] border border-[#1E293B] rounded-md p-3 cursor-pointer hover:bg-[#0F172A]" data-testid="bulk-bk-keep-position-row">
            <input
              type="checkbox"
              checked={keepPosition}
              onChange={(e) => setKeepPosition(e.target.checked)}
              className="h-4 w-4 mt-0.5 accent-[#3B82F6]"
              data-testid="bulk-bk-keep-position"
            />
            <div className="flex-1">
              <div className="font-medium">Keep same flatplan position</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">
                Place each new booking on the same page and spot as the source
                {booking.page_number ? ` (page ${booking.page_number})` : ''}.
                Leave unchecked to create unplaced bookings.
              </div>
            </div>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]">Cancel</Button>
            <Button type="button" disabled={submitting || selectedIds.length === 0} onClick={handleSubmit} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="bulk-bk-submit">
              {submitting ? 'Creating…' : `Create ${selectedIds.length} Booking${selectedIds.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Booking Modal Component
const BookingModal = ({ isOpen, onClose, booking, advertisers, editions, productTypes, settings, salesReps, onSave }) => {
  const defaultTaxRate = settings?.default_tax_rate || 16;
  const [formData, setFormData] = useState({
    advertiser_id: '',
    category: 'digital_edition',
    edition_id: '',
    edition_ids: [],
    product_type: '',
    size: '',
    rate: 0,
    page_preference: '',
    special_instructions: '',
    is_recurring: false,
    is_taxable: false,
    tax_rate: 0,
    needs_factura: false,
    sales_rep_id: '',
    status: 'draft',
    creative_file: '',
    creative_filename: '',
    digital_start_date: '',
    digital_end_date: '',
    digital_schedule_notes: '',
    // Batch scheduling (website + create-only)
    quantity: 1,
    batch_schedule_type: 'none',  // 'none' | 'single_day_weekly_repeat' | 'date_range_consecutive'
    repeat_every_weeks: 1,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editionPopoverOpen, setEditionPopoverOpen] = useState(false);
  const fileInputRef = useRef(null);

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'production', label: 'Production' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'paid', label: 'Paid' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Filter product types by selected category (memoized)
  const filteredProductTypes = useMemo(
    () => productTypes.filter(pt => pt.category === formData.category),
    [productTypes, formData.category]
  );

  useEffect(() => {
    if (booking) {
      setFormData({
        advertiser_id: booking.advertiser_id || '',
        category: booking.category || 'digital_edition',
        edition_id: booking.edition_id || '',
        edition_ids: booking.edition_id ? [booking.edition_id] : [],
        product_type: booking.product_type || '',
        size: booking.size || '',
        rate: booking.rate || 0,
        page_preference: booking.page_preference || '',
        special_instructions: booking.special_instructions || '',
        is_recurring: booking.is_recurring || false,
        is_taxable: booking.is_taxable || false,
        tax_rate: booking.tax_rate || 0,
        needs_factura: booking.needs_factura || false,
        sales_rep_id: booking.sales_rep_id || '',
        status: booking.status || 'draft',
        creative_file: booking.creative_file || '',
        creative_filename: booking.creative_filename || '',
        digital_start_date: booking.digital_start_date || '',
        digital_end_date: booking.digital_end_date || '',
        digital_schedule_notes: booking.digital_schedule_notes || '',
        quantity: 1,
        batch_schedule_type: 'none',
        repeat_every_weeks: 1,
      });
    } else {
      setFormData({
        advertiser_id: '',
        category: 'digital_edition',
        edition_id: '',
        edition_ids: [],
        product_type: '',
        size: '',
        rate: 0,
        page_preference: '',
        special_instructions: '',
        is_recurring: false,
        is_taxable: false,
        tax_rate: 0,
        needs_factura: false,
        sales_rep_id: '',
        status: 'draft',
        creative_file: '',
        creative_filename: '',
        digital_start_date: '',
        digital_end_date: '',
        digital_schedule_notes: '',
        quantity: 1,
        batch_schedule_type: 'none',
        repeat_every_weeks: 1,
      });
    }
  }, [booking]);

  // Update rate when product type changes
  useEffect(() => {
    if (!booking && formData.product_type) {
      const productType = productTypes.find(pt => pt.name === formData.product_type);
      if (productType) {
        setFormData(prev => ({ 
          ...prev, 
          rate: productType.rate || 0,
          size: productType.width && productType.height ? `${productType.width}x${productType.height}` : ''
        }));
      }
    }
  }, [formData.product_type, productTypes, booking]);

  // Reset product type when category changes
  useEffect(() => {
    if (!booking) {
      setFormData(prev => ({ ...prev, product_type: '', rate: 0 }));
    }
  }, [formData.category, booking]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      alert('Only PDF, JPG, and PNG files are accepted.');
      return;
    }

    setUploading(true);
    try {
      const result = await api.upload('/upload', file);
      setFormData(prev => ({
        ...prev,
        creative_file: result.url,
        creative_filename: result.filename
      }));
    } catch (error) {
      console.error('Upload failed:', error);
      alert('File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFormData(prev => ({ ...prev, creative_file: '', creative_filename: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.creative_file) {
        payload.creative_file = null;
        payload.creative_filename = null;
      }

      // Digital scheduling normalization + client-side validation
      if (isWebsite) {
        if (isSingleDayProduct) {
          // Single-day → end = start (or both empty)
          payload.digital_end_date = payload.digital_start_date || null;
          if (!payload.digital_start_date) {
            payload.digital_start_date = null;
          }
        } else {
          if (payload.digital_start_date && payload.digital_end_date &&
              payload.digital_end_date < payload.digital_start_date) {
            alert('Digital end date cannot be before start date.');
            setSaving(false);
            return;
          }
          payload.digital_start_date = payload.digital_start_date || null;
          payload.digital_end_date = payload.digital_end_date || null;
        }
        payload.digital_schedule_notes = payload.digital_schedule_notes || null;
      } else {
        payload.digital_start_date = null;
        payload.digital_end_date = null;
        payload.digital_schedule_notes = null;
      }

      if (booking) {
        // Edit mode — single booking
        delete payload.edition_ids;
        delete payload.quantity;
        delete payload.batch_schedule_type;
        delete payload.repeat_every_weeks;
        await api.put(`/bookings/${booking.id}`, payload);
      } else if (isWebsite && (qty > 1 || batchScheduleType !== 'none')) {
        // Batch create for website bookings
        const batchPayload = {
          ...payload,
          quantity: qty,
          batch_schedule_type: batchScheduleType,
          repeat_every_weeks: Math.max(1, Math.min(52, parseInt(formData.repeat_every_weeks, 10) || 1)),
        };
        delete batchPayload.edition_ids;
        const res = await api.post('/bookings/batch', batchPayload);
        alert(`${res.quantity} booking(s) created for ${
          (advertisers.find(a => a.id === payload.advertiser_id) || {}).business_name || 'this advertiser'
        }.`);
      } else {
        // Create mode — single booking (multi-edition supported for print)
        if (isDigitalEdition && payload.edition_ids.length > 0) {
          payload.edition_id = null; // Use edition_ids instead
        }
        delete payload.quantity;
        delete payload.batch_schedule_type;
        delete payload.repeat_every_weeks;
        await api.post('/bookings', payload);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save booking:', error);
      alert(error?.response?.data?.detail || 'Failed to save booking.');
    } finally {
      setSaving(false);
    }
  };

  const isDigitalEdition = formData.category === 'digital_edition';
  const isWebsite = formData.category === 'website';
  const selectedProductType = productTypes.find(pt => pt.name === formData.product_type);
  const isSingleDayProduct = !!selectedProductType?.single_day_product;

  // ── Batch-schedule preview (client-side mirror of backend logic) ─────────
  const batchScheduleType = formData.batch_schedule_type || 'none';
  const qty = Math.max(1, Math.min(50, parseInt(formData.quantity, 10) || 1));
  const previewDates = React.useMemo(() => {
    if (!isWebsite || qty <= 1) return [];
    // Helpers
    const parse = (s) => {
      if (!s) return null;
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, (m || 1) - 1, d || 1);
    };
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const addDaysL = (d, n) => { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; };
    const addMonthsL = (d, n) => {
      const y = d.getFullYear();
      const m = d.getMonth() + n;
      const targetYear = y + Math.floor(m / 12);
      const targetMonth = ((m % 12) + 12) % 12;
      const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
      return new Date(targetYear, targetMonth, Math.min(d.getDate(), lastDay));
    };
    const isMonthlyCycle = (s, e) => {
      const next = addMonthsL(s, 1);
      const expectedEnd = addDaysL(next, -1);
      return expectedEnd.getTime() === e.getTime();
    };

    const s = parse(formData.digital_start_date);
    const e = parse(formData.digital_end_date) || (isSingleDayProduct ? s : null);

    if (batchScheduleType === 'single_day_weekly_repeat') {
      if (!s || !isSingleDayProduct) return [];
      const weeks = Math.max(1, Math.min(52, parseInt(formData.repeat_every_weeks, 10) || 1));
      return Array.from({ length: qty }, (_, n) => {
        const d = addDaysL(s, 7 * weeks * n);
        return { start: fmt(d), end: fmt(d) };
      });
    }
    if (batchScheduleType === 'date_range_consecutive') {
      if (!s || !e || isSingleDayProduct || e < s) return [];
      const monthly = isMonthlyCycle(s, e);
      const out = [{ start: fmt(s), end: fmt(e) }];
      let prevStart = s, prevEnd = e;
      for (let i = 1; i < qty; i++) {
        const nextStart = addDaysL(prevEnd, 1);
        let nextEnd;
        if (monthly) {
          nextEnd = addMonthsL(prevEnd, 1);
        } else {
          const durDays = Math.round((prevEnd - prevStart) / 86400000) + 1;
          nextEnd = addDaysL(nextStart, durDays - 1);
        }
        out.push({ start: fmt(nextStart), end: fmt(nextEnd) });
        prevStart = nextStart;
        prevEnd = nextEnd;
      }
      return out;
    }
    // schedule_type == 'none' → repeat same dates
    const sIso = s ? fmt(s) : null;
    const eIso = e ? fmt(e) : sIso;
    return Array.from({ length: qty }, () => ({ start: sIso, end: eIso }));
  }, [
    isWebsite, qty, batchScheduleType, formData.digital_start_date,
    formData.digital_end_date, formData.repeat_every_weeks, isSingleDayProduct,
  ]);

  const [advSearchOpen, setAdvSearchOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{booking ? 'Edit Booking' : 'New Booking'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <NewsdeskSentInfo booking={booking} />
          <SentControl booking={booking} onChanged={onSave} />
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Advertiser *</Label>
            <Popover open={advSearchOpen} onOpenChange={setAdvSearchOpen}>
              <PopoverTrigger asChild disabled={!!booking}>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] hover:bg-[#1A1A1A]"
                  data-testid="booking-advertiser-select"
                >
                  {formData.advertiser_id
                    ? advertisers.find(a => a.id === formData.advertiser_id)?.business_name || 'Select advertiser'
                    : 'Select advertiser'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0 bg-[#121212] border-[#1E293B]" align="start">
                <Command className="bg-[#121212]">
                  <CommandInput placeholder="Search advertisers..." className="text-[#F8FAFC]" data-testid="advertiser-search-input" />
                  <CommandList>
                    <CommandEmpty className="text-[#94A3B8] text-sm py-4 text-center">No advertiser found.</CommandEmpty>
                    <CommandGroup>
                      {[...advertisers].sort((a, b) => a.business_name.localeCompare(b.business_name)).map(adv => (
                        <CommandItem
                          key={adv.id}
                          value={adv.business_name}
                          onSelect={() => { setFormData(prev => ({ ...prev, advertiser_id: adv.id })); setAdvSearchOpen(false); }}
                          className="text-[#F8FAFC] cursor-pointer"
                        >
                          <Check className={`mr-2 h-4 w-4 ${formData.advertiser_id === adv.id ? 'opacity-100 text-[#3B82F6]' : 'opacity-0'}`} />
                          {adv.business_name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Sales Rep */}
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Sales Rep</Label>
            <Select value={formData.sales_rep_id || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, sales_rep_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="booking-sales-rep-select">
                <SelectValue placeholder="Select sales rep" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="none" className="text-[#94A3B8]">No sales rep</SelectItem>
                {[...salesReps].sort((a, b) => a.name.localeCompare(b.name)).map(rep => (
                  <SelectItem key={rep.id} value={rep.id} className="text-[#F8FAFC]">{rep.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category Selection */}
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
              disabled={!!booking}
            >
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="booking-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="digital_edition" className="text-[#F8FAFC]">
                  <div className="flex items-center gap-2">
                    <Newspaper className="w-4 h-4" />
                    Print Edition
                  </div>
                </SelectItem>
                <SelectItem value="website" className="text-[#F8FAFC]">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Website
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Edition - Only for Print Edition */}
          {isDigitalEdition && (
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">{booking ? 'Edition' : 'Edition(s) *'}</Label>
              {booking ? (
                <Input value={booking.edition_name || ''} disabled className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" />
              ) : (
                <Popover open={editionPopoverOpen} onOpenChange={setEditionPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] hover:bg-[#1A1A1A] font-normal"
                      data-testid="booking-edition-select"
                    >
                      {formData.edition_ids.length > 0
                        ? `${formData.edition_ids.length} edition${formData.edition_ids.length > 1 ? 's' : ''} selected`
                        : 'Select editions'}
                      <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-0 bg-[#121212] border-[#1E293B]">
                    <div className="p-2 border-b border-[#1E293B]">
                      <label className="flex items-center gap-2 px-2 py-1 cursor-pointer text-sm text-[#94A3B8] hover:text-[#F8FAFC]">
                        <Checkbox
                          checked={formData.edition_ids.length === editions.filter(e => !e.is_locked).length && editions.filter(e => !e.is_locked).length > 0}
                          onCheckedChange={(checked) => {
                            const allIds = editions.filter(e => !e.is_locked).map(e => e.id);
                            setFormData(prev => ({ ...prev, edition_ids: checked ? allIds : [] }));
                          }}
                        />
                        Select All
                      </label>
                    </div>
                    <div
                      className="max-h-72 overflow-y-auto p-2 space-y-1 overscroll-contain"
                      onWheelCapture={(e) => {
                        // Let the container consume wheel events even when inside a Dialog/FocusScope
                        e.currentTarget.scrollTop += e.deltaY;
                        e.stopPropagation();
                      }}
                      data-testid="booking-edition-options"
                    >
                      {editions.filter(e => !e.is_locked).map(ed => (
                        <label key={ed.id} className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm text-[#F8FAFC] hover:bg-[#1E293B]">
                          <Checkbox
                            checked={formData.edition_ids.includes(ed.id)}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({
                                ...prev,
                                edition_ids: checked
                                  ? [...prev.edition_ids, ed.id]
                                  : prev.edition_ids.filter(id => id !== ed.id)
                              }));
                            }}
                          />
                          <span>{ed.name}</span>
                          <span className="text-xs text-[#94A3B8] ml-auto">{formatDate(ed.publish_date)}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Product Type *</Label>
              <Select
                value={formData.product_type}
                onValueChange={(value) => setFormData({ ...formData, product_type: value })}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="booking-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {[...filteredProductTypes].sort((a, b) => a.name.localeCompare(b.name)).map(pt => (
                    <SelectItem key={pt.id} value={pt.name} className="text-[#F8FAFC]">
                      {pt.name} - {formatCurrency(pt.rate)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Rate ($)</Label>
              <Input
                data-testid="booking-rate-input"
                type="number"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              />
            </div>
          </div>

          {isDigitalEdition && (
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Page Preference</Label>
              <Input
                data-testid="booking-page-pref-input"
                value={formData.page_preference}
                onChange={(e) => setFormData({ ...formData, page_preference: e.target.value })}
                placeholder="e.g., Front section, right-hand page"
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              />
            </div>
          )}

          {isWebsite && !booking && (
            <div
              className="p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3"
              data-testid="booking-quantity-section"
            >
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wide text-[#94A3B8]">
                  Quantity &amp; batch scheduling
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[#94A3B8] text-xs">Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    step={1}
                    value={formData.quantity}
                    onChange={(e) => {
                      const raw = e.target.value === '' ? '' : Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1));
                      setFormData({ ...formData, quantity: raw });
                    }}
                    onBlur={(e) => {
                      if (!e.target.value || parseInt(e.target.value, 10) < 1) {
                        setFormData((prev) => ({ ...prev, quantity: 1 }));
                      }
                    }}
                    data-testid="booking-quantity-input"
                    className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
                  />
                </div>
              </div>

              {qty > 1 && isSingleDayProduct && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F8FAFC]">
                    <Checkbox
                      checked={batchScheduleType === 'single_day_weekly_repeat'}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                          ...prev,
                          batch_schedule_type: checked ? 'single_day_weekly_repeat' : 'none',
                        }))
                      }
                      data-testid="booking-repeat-weekly-checkbox"
                    />
                    <span>Repeat on same day of week</span>
                  </label>
                  {batchScheduleType === 'single_day_weekly_repeat' && (
                    <div className="grid grid-cols-2 gap-3 pl-6">
                      <div className="space-y-1">
                        <Label className="text-[#94A3B8] text-xs">Every N weeks</Label>
                        <Input
                          type="number"
                          min={1}
                          max={52}
                          step={1}
                          value={formData.repeat_every_weeks}
                          onChange={(e) => setFormData({
                            ...formData,
                            repeat_every_weeks: Math.max(1, Math.min(52, parseInt(e.target.value, 10) || 1)),
                          })}
                          data-testid="booking-repeat-every-weeks-input"
                          className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {qty > 1 && !isSingleDayProduct && formData.product_type && (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F8FAFC]">
                  <Checkbox
                    checked={batchScheduleType === 'date_range_consecutive'}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        batch_schedule_type: checked ? 'date_range_consecutive' : 'none',
                      }))
                    }
                    data-testid="booking-consecutive-checkbox"
                  />
                  <span>Book consecutive date ranges</span>
                </label>
              )}

              {qty > 1 && previewDates.length > 0 && (
                <div
                  className="p-2 bg-[#121212] border border-[#1E293B] rounded text-xs space-y-0.5 max-h-40 overflow-y-auto"
                  data-testid="booking-batch-preview"
                >
                  <p className="text-[#94A3B8] mb-1">
                    This will create <span className="text-[#F8FAFC] font-medium">{qty}</span> booking(s):
                  </p>
                  {previewDates.map((d, i) => (
                    <p
                      key={`${d.start || 'unscheduled'}-${d.end || ''}-${i}`}
                      className="text-[#F8FAFC] font-mono text-[11px]"
                      data-testid={`booking-batch-preview-row-${i}`}
                    >
                      #{i + 1} · {d.start
                        ? (d.end && d.end !== d.start ? `${d.start} → ${d.end}` : d.start)
                        : <span className="text-amber-400">unscheduled</span>}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}


          {isWebsite && (
            <div
              className="p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3"
              data-testid="booking-digital-schedule-section"
            >
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wide text-[#94A3B8]">
                  Digital schedule
                </p>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1E293B] text-[#94A3B8]"
                  data-testid="booking-schedule-mode"
                >
                  {formData.product_type
                    ? (isSingleDayProduct ? 'Single-day' : 'Date range')
                    : 'Select a product first'}
                </span>
              </div>
              {formData.product_type && (
                <>
                  {isSingleDayProduct ? (
                    <div className="space-y-1">
                      <Label className="text-[#94A3B8] text-xs">Scheduled date</Label>
                      <Input
                        type="date"
                        value={formData.digital_start_date}
                        onChange={(e) => setFormData({ ...formData, digital_start_date: e.target.value })}
                        className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
                        data-testid="booking-digital-scheduled-date-input"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[#94A3B8] text-xs">Start date</Label>
                        <Input
                          type="date"
                          value={formData.digital_start_date}
                          onChange={(e) => setFormData({ ...formData, digital_start_date: e.target.value })}
                          className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
                          data-testid="booking-digital-start-date-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[#94A3B8] text-xs">End date</Label>
                        <Input
                          type="date"
                          value={formData.digital_end_date}
                          onChange={(e) => setFormData({ ...formData, digital_end_date: e.target.value })}
                          min={formData.digital_start_date || undefined}
                          className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
                          data-testid="booking-digital-end-date-input"
                        />
                      </div>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-[#94A3B8] text-xs">Schedule notes</Label>
                    <Input
                      value={formData.digital_schedule_notes}
                      onChange={(e) => setFormData({ ...formData, digital_schedule_notes: e.target.value })}
                      placeholder="Campaign name, targeting, delivery notes"
                      className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
                      data-testid="booking-digital-schedule-notes-input"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tax Settings */}
          <div className="p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 flex-1">
                <Switch
                  checked={formData.is_taxable}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_taxable: checked, tax_rate: checked ? prev.tax_rate || defaultTaxRate : 0, needs_factura: checked ? prev.needs_factura : false }))}
                  data-testid="booking-taxable-switch"
                />
                <Label className="text-[#94A3B8] text-sm">Taxable</Label>
              </div>
              {formData.is_taxable && (
                <div className="flex items-center gap-2">
                  <Label className="text-[#94A3B8] text-sm">Tax Rate</Label>
                  <Input
                    data-testid="booking-tax-rate-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                    className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] w-20 h-8 text-sm"
                  />
                  <span className="text-[#94A3B8] text-sm">%</span>
                </div>
              )}
            </div>
            {formData.is_taxable && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.needs_factura}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, needs_factura: checked }))}
                  data-testid="booking-needs-factura"
                />
                <span className="text-[#94A3B8] text-sm">Client needs factura</span>
              </label>
            )}
          </div>

          {/* Ad Creative Upload */}
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Ad Creative</Label>
            {formData.creative_file ? (
              <div className="border border-[#1E293B] rounded-md p-3 bg-[#0A0A0A]">
                <div className="flex items-start gap-3">
                  {isImageFile(formData.creative_filename) ? (
                    <img
                      src={`${API_BASE}${formData.creative_file}`}
                      alt="Ad creative preview"
                      className="w-24 h-24 object-cover rounded border border-[#1E293B]"
                      data-testid="creative-thumbnail"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-[#1E293B] rounded flex flex-col items-center justify-center" data-testid="creative-pdf-icon">
                      <FileText className="w-8 h-8 text-[#3B82F6]" />
                      <span className="text-[10px] text-[#94A3B8] mt-1">PDF</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#F8FAFC] truncate">{formData.creative_filename}</p>
                    <a
                      href={`${API_BASE}${formData.creative_file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#3B82F6] hover:underline"
                      data-testid="creative-view-link"
                    >
                      View full size
                    </a>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeFile}
                    className="text-red-400 hover:text-red-300 p-1"
                    data-testid="remove-creative-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className="border border-dashed border-[#1E293B] rounded-md p-4 text-center cursor-pointer hover:border-[#3B82F6]/50 transition-colors bg-[#0A0A0A]"
                data-testid="creative-upload-area"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B82F6]"></div>
                    <span className="text-sm text-[#94A3B8]">Uploading...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-6 h-6 text-[#475569]" />
                    <span className="text-sm text-[#94A3B8]">Click to upload ad creative</span>
                    <span className="text-xs text-[#475569]">PDF, JPG, or PNG</span>
                  </div>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="creative-file-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Special Instructions</Label>
            <Textarea
              data-testid="booking-instructions-input"
              value={formData.special_instructions}
              onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
              placeholder="Any special requirements..."
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] min-h-[80px]"
            />
          </div>

          {/* Status dropdown - only show when editing */}
          {booking && (
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="booking-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {statusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-[#F8FAFC]">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving || uploading || !formData.advertiser_id || !formData.product_type || (isDigitalEdition && !booking && formData.edition_ids.length === 0)}
              data-testid="save-booking-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              {saving ? 'Saving...' : (booking ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingsPage;
